<template>
  <div class="data-table management-main-list-data-table">
    <slot />

    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">
          {{ $tc("Template", 2) }}
        </span>

        <v-spacer />

        <v-text-field
          :label="$t('Search')"
          :placeholder="$t('Search')"
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          class="data-table-search"
          single-line
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="templates"
        :search="searchFromParent"
        :loading="loading"
        loading-text=""
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :items-per-page.sync="paginationReservationTemplate.rowsPerPage"
        :class="{ 'no-show-permission': !userHasPermissionTo('show') }"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon, 'items-per-page-options':rowsPerPageItems }"
        @pagination="setRowsOnLocalStorage(dataTableReservationTemplateListId,paginationReservationTemplate.rowsPerPage)"
      >
        <template v-slot:item="props">
          <tr
            @click="viewItem(props.item.art_uuid)"
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
          >
            <td>{{ props.item.art_label }}</td>

            <td>{{ props.item.owner_name }}</td>

            <td>
              {{ getPossibleLayers(props.item) }}
            </td>

            <td>
              {{ getPossibleStatus(props.item.art_status) ? getPossibleStatus(props.item.art_status) : '-' }}
            </td>

            <td>
              {{ props.item.art_active ? $t("Yes") : $t("No") }}
            </td>

            <td>
              {{ props.item.art_index }}
            </td>

            <td v-if="userCanEditOrRemoveItems">
              <app-datatable-action-buttons
                :user-can-edit="userHasPermissionTo('update')"
                @edit="editItem(props.item.art_uuid)"
                :user-can-delete="userHasPermissionTo('destroy') && props.item.active_asset_reservations_count === 0"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>

        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-results>
          <data-table-no-results :search="search" />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />
        </template>
      </v-data-table>
      <app-dialog-box
        :open="deleteDialog"
        :title="$t('DeleteItem')"
        :content="
          $t('DeleteConfirmation') +
            ' ' +
            $tc('Template', 1) +
            ' ' +
            itemToDelete.art_label +
            '?'
        "
        :main-action-label="$t('No')"
        @mainAction="cancelDelete"
        :secondary-action-label="$t('Yes')"
        @secondaryAction="confirmDelete"
        :loading="loadingDelete"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";

export default {
  name: "ReservationTemplatesList",

  components: { DataTableNoResults, AppDatatableActionButtons, AppDialogBox },

  mixins: [managementDatatableMixin],

  data() {
    return {
      /**
       * Permission name referring to the functionality of templates
       */
      permissionName: "asset_reservation_template",
      /**
       * Sets the loading of the delete a template dialog button
       */
      loadingDelete: false,
      dataTableReservationTemplateListId: "dataTableReservationTemplateListId" ,
      paginationReservationTemplate: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableReservationTemplateListId")
      },
      /**
       * List of templates
       */
      templates: [],
      statusList:[]
    };
  },

  computed: {
    /**
     * Determines table headers for templates
     * {String} value - Column key
     * {String} text - Column title
     */
    mainHeaders() {
      return [
        {
          text: this.$i18n.tc("Label", 1),
          value: "art_label"
        },
        {
          text: this.$i18n.tc("Owner", 1),
          value: "owner_name"
        },
        {
          text: this.$i18n.tc("Layer", 2),
          value: "art_accepted_layers"
        },
        {
          text: this.$i18n.t("Status"),
          value: "art_status"
        },
        {
          text: this.$i18n.t("Active"),
          value: "art_active"
        },
        {
          text: this.$i18n.t("Index"),
          value: "art_index"
        }
      ];
    }
  },

  async mounted() {
    await this.getAssetReservationTemplates();
    await this.getListStatus();
  },

  methods: {
    ...mapActions([
      "fetchAssetReservationTemplates",
      "deleteAssetReservationTemplate",
      "GET_LIST"
    ]),

    /**
     * Get the list of templates
     */
    async getAssetReservationTemplates() {
      this.loading = true;

      try {
        this.templates = await this.fetchAssetReservationTemplates(
          [
            "owner_name", 
            "active_asset_reservations_count"
          ]
        );
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Get the translation of the layers
     *
     * @param {String} template
     * @return {String} Returns the translation of the layers
     */
    getPossibleLayers(template) {
      if (template) {
        let layers = template.art_accepted_layers;

        if (layers === "all") {
          return "All";
        } else {
          layers = layers.split(",");

          let layerNames = [];
          for (const layer of layers) {
            layerNames.push(this.$store.getters.getLayerTranslationKey(layer));
          }

          return layerNames.join(", ");
        }
      } else {
        return null;
      }
    },
    async getListStatus(){
      this.statusList = await this.GET_LIST({
            listName: "list_avancement",
            includeDeleted: true
          });
    },

    /**
     * Get status label from list_statut list
     * @param statusCode
     * @returns {string|null|*|string}
     */
    getPossibleStatus(statusCode) {
      if(statusCode){
        const statusArray = statusCode.split(',')
        let status = [];
        let listName = "list_avancement";
        let storedList = this.$store.getters.getListOfOptionsByListName(
          listName,
          [statusCode],
          true
        );
        if(storedList){
          statusArray.forEach(element =>{
            status.push(storedList.find(item => item.code === element));
          })
        }else {
          status = this.statusList.find(item => item.code === statusCode);
        }
        if (status){
          let statusString = [];
          status.forEach(item =>{
            if(item){
              const label = item.client_label ? item.client_label : item.libelle;
              statusString.push(label);
            }
          })
          return statusString.join(', ');
        }
      }
    },

    /**
     * Return list by code and name
     * @param listName
     * @param statusCode
     * @returns {Promise<unknown>}
     */
    async getList(listName, statusCode) {
      return await this.GET_LIST({
        listName: listName,
        code: [statusCode],
        includeDeleted: true
      });
    },

    /**
     * Push to the page to edit the selected template
     * @param {Object} item - Selected template
     */
    editItem(item) {
      if (this.userHasPermissionTo("update")) {
        /**
         * @property {String} name - Name of the route
         * @property {Object} params - Parameters needed for the route path
         */
        this.$router.push({
          name: "reservation-template-edit",
          params: { uuid: item }
        });
      }
    },

    /**
     * Push to the page to see the selected template
     * @param {Object} item - Selected template
     */
    viewItem(item) {
      if (this.userHasPermissionTo("show")) {
        /**
         * @property {String} name - Name of the route
         * @property {Object} params - Parameters needed for the route path
         */
        this.$router.push({
          name: "reservation-template",
          params: { uuid: item }
        });
      }
    },

    /**
     * Sends a request to delete the selected template and closes the delete confirmation dialog.
     */
    async confirmDelete() {
      try {
        this.loadingDelete = true;
        await this.deleteAssetReservationTemplate(this.itemToDelete.art_uuid);
        this.itemToDelete = {};
        this.deleteDialog = false;
        await this.getAssetReservationTemplates();
      } catch (error) {
        this.$toasted.global.toast_error(error.data.message);
        console.error(error);
      } finally {
        this.loadingDelete = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AssetReservationManagement/Templates/ReservationTemplatesList.vue